import type {
  EmailLookupResponse,
  ResetPasswordPayload,
  ResetPasswordResponse,
  SendPasswordResetPayload,
  SendPasswordResetResponse,
  SignInSuccess,
  SignUpResponse,
  SocialAuthProvider,
  SocialProvidersResponse,
  SocialSignInPayload,
  SocialValidateResponse,
} from '@/core/models/auth';
import type {
  BffCatalogProduct,
  BffCatalogProductAvailability,
  BffCatalogProductsResponse,
  BffCatalogSubscription,
  BffCatalogSubscriptionsResponse,
  BffStockNotificationBody,
} from '@/core/models/bff-catalog';
import type {
  BffFaqAvailableFor,
  BffFaqsResponse,
  BffFaqTopic,
} from '@/core/models/bff-faqs';
import type {
  BffOrderDetailResponse,
  BffOrderInvoiceDownloadResponse,
  BffOrderInvoicesResponse,
  BffOrdersListResponse,
} from '@/core/models/bff-orders';
import type {
  AvailabilityRequest,
  AvailabilityResponse,
  Booking,
  BookingResponse,
  BookingsQueryParams,
  BookingsResponse,
  CreateBookingRequest,
  GuestBooking,
  GuestBookingResponse,
  Location,
  LocationResponse,
  LocationsResponse,
  Service,
  ServicesResponse,
  UpdateBookingByCodeRequest,
  UpdateBookingRequest,
} from '@/core/models/booking';
import type {
  CreateSupportCaseRequest,
  SupportCaseResponse,
  SupportConfiguration,
  SupportConfigurationResponse,
} from '@/core/models/support';
import type {
  ProfileAddress,
  UserProfile,
  UserProfileResponse,
} from '@/core/models/user';
import type { SignInPayload } from '@/core/schemas/sign-in-schema';
import type { SignUpSchema } from '@/core/schemas/sign-up-schema';
import {
  BACKEND_API_KEY,
  BACKEND_API_URL,
  CONTACT_API_KEY,
  ENDPOINTS,
  GCP_RITUALS_API_URL,
  GI_API_URL,
  TENANT_ID,
} from '@/core/constants/endpoints';
import { SUPPORT_FAQS_REVALIDATE_SECONDS } from '@/core/constants/support-faqs-cache';
import parseLocale from '@/core/i18n/parse-locale';
import {
  type BffAddressDetailApiRaw,
  type BffCartAddress,
  type BffCreatePaymentBody,
  type BffCreatePaymentResponse,
  type BffCreateShoppingCartBody,
  type BffFindAddressesResponse,
  type BffGetAddressByReferenceResponse,
  type BffPutCartCouponBody,
  type BffPutCartProductsBody,
  type BffPutCartSubscriptionsBody,
  type BffShoppingCartResponse,
  normalizeBffAddressDetail,
  normalizeBffShoppingCartResponse,
} from '@/core/models/bff-shopping-carts';
import {
  type BffSubscriptionDetailResponse,
  type BffSubscriptionMemberInviteResponse,
  type BffSubscriptionMembersResponse,
  type BffSubscriptionRedeemResponse,
  type BffSubscriptionsListResponse,
  type BffSubscriptionWire,
  normalizeBffSubscription,
} from '@/core/models/bff-subscriptions';
import { normalizeBffPlanInterval } from '@/core/utils/catalog/normalize-purchasable-plans';
import createApi from '@/core/utils/create-api';

const headersWithBackendApiKey = {
  'x-apikey': BACKEND_API_KEY,
};

const createBffApi = () =>
  createApi({
    endpoint: BACKEND_API_URL,
    headers: headersWithBackendApiKey,
    credentials: 'include',
  });

async function signIn(credentials: SignInPayload): Promise<SignInSuccess> {
  if (!credentials.password) {
    throw new Error('Password is required to sign in');
  }

  await createBffApi().post<SignInSuccess>(
    ENDPOINTS.signInPassword,
    { email: credentials.email, password: credentials.password },
    { skipRefreshToken: true },
  );
}

async function getSocialProviders(): Promise<SocialProvidersResponse> {
  return createBffApi().get<SocialProvidersResponse>(ENDPOINTS.socialProviders, {
    skipRefreshToken: true,
  });
}

async function validateSocialLogin(
  idToken: string,
): Promise<SocialValidateResponse> {
  return createBffApi().post<SocialValidateResponse>(
    ENDPOINTS.socialValidate,
    { idToken },
    { skipRefreshToken: true },
  );
}

/** Links a social identity to the signed-in account (204, session required). */
async function linkSocialAccount(idToken: string): Promise<void> {
  await createBffApi().post<void>(ENDPOINTS.socialLink, { idToken });
}

/** Removes a linked social identity from the signed-in account (204). */
async function unlinkSocialAccount(
  provider: SocialAuthProvider,
): Promise<void> {
  await createBffApi().delete<void>(`${ENDPOINTS.socialLink}/${provider}`);
}

async function signInWithIdToken(
  payload: SocialSignInPayload,
): Promise<SignInSuccess> {
  await createBffApi().post<SignInSuccess>(
    ENDPOINTS.signInIdToken,
    {
      idToken: payload.idToken,
      authorizationCode: payload.authorizationCode,
    },
    { skipRefreshToken: true },
  );
}

async function signUp(
  credentials: SignUpSchema,
): Promise<SignUpResponse | SignInSuccess> {
  const response = await createBffApi().post<SignUpResponse>(ENDPOINTS.profile, credentials, {
    skipRefreshToken: true,
  });
  return response;
}

async function getUserProfile(): Promise<UserProfile> {
  const response = await createBffApi().get<UserProfileResponse>(ENDPOINTS.profile);
  return response.data;
}

async function updateUserProfile(
  data: Omit<UserProfile, 'EmailAddress'>,
): Promise<void> {
  await createBffApi().put<void>(ENDPOINTS.profile, data);
}

async function putUserProfileShippingAddress(
  address: ProfileAddress,
): Promise<UserProfileResponse> {
  return createBffApi().put<UserProfileResponse>(
    ENDPOINTS.profileShippingAddress,
    address as Record<string, unknown>,
  );
}

async function putUserProfileBillingAddress(
  address: ProfileAddress,
): Promise<UserProfileResponse> {
  return createBffApi().put<UserProfileResponse>(
    ENDPOINTS.profileBillingAddress,
    address as Record<string, unknown>,
  );
}

async function checkEmailExists(email: string): Promise<EmailLookupResponse> {
  const response = await createBffApi().post<EmailLookupResponse>(
    ENDPOINTS.validate,
    {
      email,
    },
  );

  return response;
}

async function sendPasswordReset(
  email: string,
  continueUrl: string,
  locale: string = 'en_NL',
): Promise<SendPasswordResetResponse> {
  const payload: SendPasswordResetPayload = {
    email,
    clientType: 'CLIENT_TYPE_WEB',
    requestType: 'PASSWORD_RESET',
    tenantId: TENANT_ID,
    continueUrl,
    locale,
  };

  const response = await createApi({
    endpoint: GCP_RITUALS_API_URL,
  }).post<SendPasswordResetResponse>(
    `${ENDPOINTS.sendOobCode}?key=${CONTACT_API_KEY}`,
    payload,
  );

  return response;
}

async function resetPassword(
  email: string,
  oobCode: string,
  newPassword: string,
): Promise<ResetPasswordResponse> {
  const payload: ResetPasswordPayload = {
    email,
    tenantId: TENANT_ID,
    oobCode,
    newPassword,
  };

  const response = await createApi({
    endpoint: GI_API_URL,
  }).post<ResetPasswordResponse>(
    `${ENDPOINTS.resetPassword}?key=${CONTACT_API_KEY}`,
    payload,
  );

  return response;
}

async function refreshToken(): Promise<SignInSuccess> {
  await createBffApi().post<SignInSuccess>(ENDPOINTS.signInRefresh, {}, { skipRefreshToken: true });
}

// Booking API functions

async function getLocations(
  latitude?: number,
  longitude?: number,
  languageCode = 'en',
): Promise<Location[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('languageCode', languageCode);
  if (latitude !== undefined && longitude !== undefined) {
    searchParams.set('latitude', String(latitude));
    searchParams.set('longitude', String(longitude));
  }

  const response = await createBffApi().get<LocationsResponse>(
    `${ENDPOINTS.locations}?${searchParams.toString()}`,
  );
  return response.data;
}

async function getLocation(
  uuid: string,
  languageCode = 'en',
): Promise<Location> {
  const response = await createBffApi().get<LocationResponse>(
    `${ENDPOINTS.locations}/${uuid}?languageCode=${languageCode}`,
  );
  return response.data;
}

async function getLocationServices(
  locationUuid: string,
  languageCode = 'en',
): Promise<Service[]> {
  const response = await createBffApi().get<ServicesResponse>(
    `${ENDPOINTS.locations}/${locationUuid}/services?languageCode=${languageCode}`,
  );
  return response.data;
}

async function checkAvailability(
  params: AvailabilityRequest,
  languageCode = 'en',
): Promise<AvailabilityResponse['data']> {
  const searchParams = new URLSearchParams();
  searchParams.set('languageCode', languageCode);
  searchParams.set('locationUuid', params.locationUuid);
  searchParams.set('mainServiceUuid', params.mainServiceUuid);
  if (params.addOnServiceUuid) {
    searchParams.set('addOnServiceUuid', params.addOnServiceUuid);
  }
  searchParams.set('date', params.date);
  searchParams.set('numberOfPeople', String(params.numberOfPeople));
  if (params.excludeBookingUuid) {
    searchParams.set('excludeBookingUuid', params.excludeBookingUuid);
  }

  const response = await createBffApi().get<AvailabilityResponse>(
    `${ENDPOINTS.availability}?${searchParams.toString()}`,
  );
  return response.data;
}

async function getBookings(
  params?: BookingsQueryParams,
  languageCode = 'en',
): Promise<Booking[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('languageCode', languageCode);
  if (params?.locationUuid) {
    searchParams.set('locationUuid', params.locationUuid);
  }
  if (params?.dateFrom) {
    searchParams.set('dateFrom', params.dateFrom);
  }
  if (params?.dateTo) {
    searchParams.set('dateTo', params.dateTo);
  }
  if (params?.serviceType) {
    searchParams.set('serviceType', params.serviceType);
  }
  if (params?.status) {
    searchParams.set('status', params.status);
  }

  const response = await createBffApi().get<BookingsResponse>(`${ENDPOINTS.bookings}?${searchParams.toString()}`);
  return response.data;
}

async function getBooking(
  uuid: string,
  languageCode = 'en',
): Promise<Booking> {
  const response = await createBffApi().get<BookingResponse>(
    `${ENDPOINTS.bookings}/${uuid}?languageCode=${languageCode}`,
  );
  return response.data;
}

async function getUserBookings(languageCode = 'en'): Promise<Booking[]> {
  const response = await createBffApi().get<BookingsResponse>(
    `${ENDPOINTS.bookings}?languageCode=${languageCode}`,
  );

  return response.data;
}

async function createBooking(
  data: CreateBookingRequest,
  languageCode = 'en',
): Promise<Booking> {
  const response = await createBffApi().post<BookingResponse>(
    `${ENDPOINTS.bookings}?languageCode=${languageCode}`,
    data,
  );
  return response.data;
}

async function updateBooking(
  uuid: string,
  data: UpdateBookingRequest,
  languageCode = 'en',
): Promise<Booking> {
  const response = await createBffApi().put<BookingResponse>(
    `${ENDPOINTS.bookings}/${uuid}?languageCode=${languageCode}`,
    data,
  );
  return response.data;
}

async function cancelBooking(
  uuid: string,
  languageCode = 'en',
): Promise<Booking> {
  const response = await createBffApi().put<BookingResponse>(
    `${ENDPOINTS.bookings}/${uuid}/cancel?languageCode=${languageCode}`,
    {},
  );
  return response.data;
}

// Booking access-code API functions (guest manage flow).
// Authorised by booking UUID + `X-Access-Code` header. No session is involved:
// credentials are omitted so the backend never sees the visitor's cookies, and
// responses are never cached.
const createGuestBffApi = () =>
  createApi({
    endpoint: BACKEND_API_URL,
    headers: headersWithBackendApiKey,
    credentials: 'omit',
  });

const accessCodeRequestConfig = (accessCode: string) => ({
  headers: { 'X-Access-Code': accessCode },
  cache: 'no-store' as const,
  skipRefreshToken: true,
});

async function getBookingByCode(
  uuid: string,
  accessCode: string,
  languageCode = 'en',
): Promise<GuestBooking> {
  const response = await createGuestBffApi().get<GuestBookingResponse>(
    `${ENDPOINTS.bookingsByCode}/${encodeURIComponent(uuid)}?languageCode=${languageCode}`,
    accessCodeRequestConfig(accessCode),
  );
  return response.data;
}

async function updateBookingByCode(
  uuid: string,
  accessCode: string,
  data: UpdateBookingByCodeRequest,
  languageCode = 'en',
): Promise<GuestBooking> {
  const response = await createGuestBffApi().put<GuestBookingResponse>(
    `${ENDPOINTS.bookingsByCode}/${encodeURIComponent(uuid)}?languageCode=${languageCode}`,
    data,
    accessCodeRequestConfig(accessCode),
  );
  return response.data;
}

async function cancelBookingByCode(
  uuid: string,
  accessCode: string,
  languageCode = 'en',
): Promise<GuestBooking> {
  const response = await createGuestBffApi().put<GuestBookingResponse>(
    `${ENDPOINTS.bookingsByCode}/${encodeURIComponent(uuid)}/cancel?languageCode=${languageCode}`,
    {},
    accessCodeRequestConfig(accessCode),
  );
  return response.data;
}

async function createSupportCase(
  data: CreateSupportCaseRequest,
  files?: File[],
): Promise<SupportCaseResponse> {
  if (files && files.length > 0) {
    const formData = new FormData();

    formData.append('firstName', data.firstName);
    formData.append('lastName', data.lastName);
    formData.append('email', data.email);
    formData.append('category', data.category);
    formData.append('topic', data.topic);
    formData.append('message', data.message);
    formData.append('preferredLanguage', data.preferredLanguage);
    formData.append('preferredCountry', data.preferredCountry);

    files.forEach(file => {
      formData.append('files[]', file);
    });

    const response = await createBffApi().post<SupportCaseResponse>(ENDPOINTS.supportCase, formData);

    return response;
  }

  // If no files, send JSON request
  const response = await createBffApi().post<SupportCaseResponse>(ENDPOINTS.supportCase, data);

  return response;
}

async function getSupportConfigurations(
  locale: string,
): Promise<SupportConfiguration> {
  const { language: languageCode } = parseLocale(locale);

  const response = await createBffApi().get<SupportConfigurationResponse>(
    `${ENDPOINTS.supportConfigurations}?locale=${languageCode}`,
  );
  return response.data;
}

async function getBffFaqs(params: {
  locale: string;
  availableFor?: BffFaqAvailableFor;
}): Promise<BffFaqTopic[]> {
  const { language: languageCode } = parseLocale(params.locale);
  const searchParams = new URLSearchParams({
    locale: languageCode || 'en',
  });
  if (params.availableFor) {
    searchParams.set('availableFor', params.availableFor);
  }

  const response = await createBffApi().get<BffFaqsResponse>(
    `${ENDPOINTS.supportFaqs}?${searchParams.toString()}`,
    {
      next: {
        revalidate: SUPPORT_FAQS_REVALIDATE_SECONDS,
        tags: ['bff-faqs', `bff-faqs-${languageCode || 'en'}`],
      },
    },
  );

  return response.data;
}

// --- BFF Orders ---
async function getBffOrders(
  languageCode = 'en',
  pageNumber = 1,
  pageSize = 20,
): Promise<BffOrdersListResponse> {
  const params = new URLSearchParams({
    languageCode,
    pageNumber: String(pageNumber),
    pageSize: String(pageSize),
  });
  const response = await createBffApi().get<BffOrdersListResponse>(`${ENDPOINTS.orders}?${params.toString()}`);
  return response;
}

async function getBffOrder(
  orderId: string,
  languageCode = 'en',
): Promise<BffOrderDetailResponse> {
  const params = new URLSearchParams({ languageCode });
  const response = await createBffApi().get<BffOrderDetailResponse>(
    `${ENDPOINTS.orders}/${orderId}?${params.toString()}`,
  );
  return response;
}

async function getBffOrderInvoices(
  orderId: string,
): Promise<BffOrderInvoicesResponse> {
  const response = await createBffApi().get<BffOrderInvoicesResponse>(`${ENDPOINTS.orders}/${orderId}/invoices`);
  return response;
}

async function getBffOrderInvoiceDownload(
  orderId: string,
  invoiceId: string,
): Promise<BffOrderInvoiceDownloadResponse> {
  const response = await createBffApi().get<BffOrderInvoiceDownloadResponse>(
    `${ENDPOINTS.orders}/${orderId}/invoices/${invoiceId}/download`,
  );
  return response;
}

// --- BFF Subscriptions (user) ---
async function getBffSubscriptions(
  languageCode = 'en',
): Promise<BffSubscriptionsListResponse> {
  const params = new URLSearchParams({ languageCode });
  const response = await createBffApi().get<{
    data: BffSubscriptionWire[];
  }>(`${ENDPOINTS.subscriptions}?${params.toString()}`);
  return {
    data: response.data.map(normalizeBffSubscription),
  };
}

async function getBffSubscription(
  subscriptionId: string,
  languageCode = 'en',
): Promise<BffSubscriptionDetailResponse> {
  const params = new URLSearchParams({ languageCode });
  const response = await createBffApi().get<{
    data: BffSubscriptionWire;
  }>(`${ENDPOINTS.subscriptions}/${subscriptionId}?${params.toString()}`);
  return {
    data: normalizeBffSubscription(response.data),
  };
}

async function pauseBffSubscription(subscriptionId: string): Promise<void> {
  await createBffApi().put<void>(`${ENDPOINTS.subscriptions}/${subscriptionId}/pause`, {});
}

async function resumeBffSubscription(subscriptionId: string): Promise<void> {
  await createBffApi().put<void>(`${ENDPOINTS.subscriptions}/${subscriptionId}/resume`, {});
}

async function cancelBffSubscription(
  subscriptionId: string,
  details?: {
    cancellationReason?: string;
    cancellationComment?: string;
  },
): Promise<void> {
  const body: Record<string, string> = {};
  if (details?.cancellationReason) {
    body.cancellationReason = details.cancellationReason;
  }
  if (details?.cancellationComment) {
    body.cancellationComment = details.cancellationComment;
  }
  await createBffApi().put<void>(
    `${ENDPOINTS.subscriptions}/${subscriptionId}/cancel`,
    body,
  );
}

async function redeemBffSubscription(
  code: string,
): Promise<BffSubscriptionRedeemResponse> {
  const response = await createBffApi().post<BffSubscriptionRedeemResponse>(`${ENDPOINTS.subscriptions}/redeem`, {
    code,
  });
  return response;
}

async function getBffSubscriptionMembers(
  subscriptionId: string,
): Promise<BffSubscriptionMembersResponse> {
  const response = await createBffApi().get<BffSubscriptionMembersResponse>(
    `${ENDPOINTS.subscriptions}/${subscriptionId}/members`,
  );
  return response;
}

async function inviteBffSubscriptionMember(
  subscriptionId: string,
  email: string,
): Promise<BffSubscriptionMemberInviteResponse> {
  const response = await createBffApi().post<BffSubscriptionMemberInviteResponse>(
    `${ENDPOINTS.subscriptions}/${subscriptionId}/members/invite`,
    { email },
  );
  return response;
}

async function removeBffSubscriptionMember(
  subscriptionId: string,
  memberUuid: string,
): Promise<void> {
  await createBffApi().delete<void>(
    `${ENDPOINTS.subscriptions}/${subscriptionId}/members/${memberUuid}`,
  );
}

// --- BFF Catalog ---
/** Raw product row: numeric `id`, `thumbnailURL`, and null `availability` are normalized. */
type BffCatalogProductWire = {
  id: number | string;
  code: string;
  type: 'Product' | 'Accessory' | 'BundledItem';
  name: string;
  description?: string;
  unitPrice: number;
  currency: string;
  thumbnailURL?: string;
  thumbnailUrl?: string;
  displayText?: string;
  displayAssets?: string[];
  maxQuantityPerOrder?: number | null;
  availability?: BffCatalogProductAvailability | null;
};

function normalizeBffCatalogProduct(
  p: BffCatalogProductWire,
): BffCatalogProduct {
  const { thumbnailURL, availability, id, ...rest } = p;
  return {
    ...rest,
    id: String(id),
    thumbnailUrl: p.thumbnailUrl ?? thumbnailURL,
    availability: availability ?? undefined,
  };
}

type BffCatalogSubscriptionWire = Omit<
  BffCatalogSubscription,
  'id' | 'thumbnailUrl' | 'code' | 'billingPrice' | 'unitPrice'
> & {
  id: number | string;
  code?: string | number;
  thumbnailURL?: string;
  thumbnailUrl?: string;
  billingPrice?: number;
  unitPrice?: number;
  contractPrice?: number;
  intervalPrice?: number;
  billingInterval?: BffCatalogSubscription['billingInterval'];
  contractInterval?: BffCatalogSubscription['contractInterval'];
  interval?: BffCatalogSubscription['contractInterval'];
  billingPeriod?: BffCatalogSubscription['billingInterval'];
  discountedUnitPrice?: number;
};

function normalizeCatalogSubscriptionInterval(
  value: string | undefined,
): BffCatalogSubscription['billingInterval'] | undefined {
  if (value == null) {
    return undefined;
  }

  const normalized = normalizeBffPlanInterval(value);
  if (
    normalized === 'weekly' ||
    normalized === 'monthly' ||
    normalized === 'quarterly' ||
    normalized === 'yearly' ||
    normalized === 'permanent'
  ) {
    return normalized;
  }

  return undefined;
}

function normalizeBffCatalogSubscription(
  s: BffCatalogSubscriptionWire,
): BffCatalogSubscription {
  const { thumbnailURL, id, code, ...rest } = s;
  const billingPrice = s.billingPrice ?? s.unitPrice ?? 0;
  const contractPrice = s.contractPrice ?? s.intervalPrice;
  const normalized: BffCatalogSubscription = {
    ...rest,
    id: String(id),
    billingPrice,
    unitPrice: billingPrice,
    thumbnailUrl: s.thumbnailUrl ?? thumbnailURL,
    billingInterval: normalizeCatalogSubscriptionInterval(
      s.billingInterval ?? s.billingPeriod,
    ),
    contractInterval: normalizeCatalogSubscriptionInterval(
      s.contractInterval ?? s.interval,
    ),
  };
  if (typeof contractPrice === 'number' && contractPrice > 0) {
    normalized.contractPrice = contractPrice;
  }
  if (code !== undefined && code !== null) {
    normalized.code = String(code);
  }
  return normalized;
}

async function getBffCatalogProducts(params: {
  countryCode: string;
  languageCode?: string;
  includeAvailability?: boolean;
}): Promise<BffCatalogProductsResponse> {
  const searchParams = new URLSearchParams({
    countryCode: params.countryCode,
    languageCode: params.languageCode ?? 'en',
  });
  if (params.includeAvailability !== undefined) {
    searchParams.set('includeAvailability', String(params.includeAvailability));
  }
  const response = await createBffApi().get<{ data: BffCatalogProductWire[] }>(
    `${ENDPOINTS.catalogProducts}?${searchParams.toString()}`,
    {
      next: {
        revalidate: 900,
        tags: [
          'bff-catalog',
          `bff-catalog-products-${params.countryCode}-${params.languageCode ?? 'en'}`,
        ],
      },
    },
  );

  return {
    data: response.data.map(normalizeBffCatalogProduct),
  };
}

async function getBffCatalogSubscriptions(params: {
  countryCode: string;
  languageCode?: string;
}): Promise<BffCatalogSubscriptionsResponse> {
  const searchParams = new URLSearchParams({
    countryCode: params.countryCode,
    languageCode: params.languageCode ?? 'en',
  });
  const response = await createBffApi().get<{
    data: BffCatalogSubscriptionWire[];
  }>(`${ENDPOINTS.catalogSubscriptions}?${searchParams.toString()}`, {
    next: {
      revalidate: 900,
      tags: [
        'bff-catalog',
        `bff-catalog-subscriptions-${params.countryCode}-${params.languageCode ?? 'en'}`,
      ],
    },
  });
  return {
    data: response.data.map(normalizeBffCatalogSubscription),
  };
}

async function createBffStockNotification(
  productId: string,
  body: BffStockNotificationBody,
): Promise<void> {
  await createBffApi().post<void>(
    `${ENDPOINTS.catalogProducts}/${productId}/stock-notifications`,
    body,
  );
}

// --- BFF Shopping carts ---
/** Cart PUT payloads require JSON numbers for `id`; catalog data uses string ids client-side. */
function catalogIdToCartPutNumber(id: string | number): number {
  if (typeof id === 'number') {
    return id;
  }
  return Number.parseInt(String(id), 10);
}

function serializeBffPutCartProductsBody(body: BffPutCartProductsBody): {
  products: { id: number; quantity: number }[];
} {
  return {
    products: body.products.map(({ id, quantity }) => ({
      id: catalogIdToCartPutNumber(id),
      quantity,
    })),
  };
}

function serializeBffPutCartSubscriptionsBody(
  body: BffPutCartSubscriptionsBody,
): { subscriptions: { id: number; quantity: number }[] } {
  return {
    subscriptions: body.subscriptions.map(({ id, quantity }) => ({
      id: catalogIdToCartPutNumber(id),
      quantity,
    })),
  };
}

async function createBffShoppingCart(params: {
  body: BffCreateShoppingCartBody;
}): Promise<BffShoppingCartResponse> {
  const response = await createBffApi().post<BffShoppingCartResponse>(ENDPOINTS.shoppingCarts, params.body);
  return normalizeBffShoppingCartResponse(response);
}

async function getBffShoppingCart(params: {
  cartId: string;
  languageCode?: string;
}): Promise<BffShoppingCartResponse> {
  const searchParams = new URLSearchParams();
  if (params.languageCode)
    searchParams.set('languageCode', params.languageCode);

  const url =
    searchParams.size > 0
      ? `${ENDPOINTS.shoppingCarts}/${params.cartId}?${searchParams.toString()}`
      : `${ENDPOINTS.shoppingCarts}/${params.cartId}`;

  const response = await createBffApi().get<BffShoppingCartResponse>(url);
  return normalizeBffShoppingCartResponse(response);
}

async function putBffShoppingCartProducts(params: {
  cartId: string;
  body: BffPutCartProductsBody;
  languageCode?: string;
}): Promise<BffShoppingCartResponse> {
  const searchParams = new URLSearchParams();
  if (params.languageCode)
    searchParams.set('languageCode', params.languageCode);

  const url =
    searchParams.size > 0
      ? `${ENDPOINTS.shoppingCarts}/${params.cartId}/products?${searchParams.toString()}`
      : `${ENDPOINTS.shoppingCarts}/${params.cartId}/products`;

  const response = await createBffApi().put<BffShoppingCartResponse>(
    url,
    serializeBffPutCartProductsBody(params.body),
  );
  return normalizeBffShoppingCartResponse(response);
}

async function putBffShoppingCartSubscriptions(params: {
  cartId: string;
  body: BffPutCartSubscriptionsBody;
  languageCode?: string;
}): Promise<BffShoppingCartResponse> {
  const searchParams = new URLSearchParams();
  if (params.languageCode)
    searchParams.set('languageCode', params.languageCode);

  const url =
    searchParams.size > 0
      ? `${ENDPOINTS.shoppingCarts}/${params.cartId}/subscriptions?${searchParams.toString()}`
      : `${ENDPOINTS.shoppingCarts}/${params.cartId}/subscriptions`;

  const response = await createBffApi().put<BffShoppingCartResponse>(
    url,
    serializeBffPutCartSubscriptionsBody(params.body),
  );
  return normalizeBffShoppingCartResponse(response);
}

async function putBffShoppingCartCoupon(params: {
  cartId: string;
  body: BffPutCartCouponBody;
  languageCode?: string;
}): Promise<BffShoppingCartResponse> {
  const searchParams = new URLSearchParams();
  if (params.languageCode)
    searchParams.set('languageCode', params.languageCode);

  const url =
    searchParams.size > 0
      ? `${ENDPOINTS.shoppingCarts}/${params.cartId}/coupon?${searchParams.toString()}`
      : `${ENDPOINTS.shoppingCarts}/${params.cartId}/coupon`;

  const response = await createBffApi().put<BffShoppingCartResponse>(url, params.body);
  return normalizeBffShoppingCartResponse(response);
}

async function putBffShoppingCartShippingAddress(params: {
  cartId: string;
  address: BffCartAddress;
  languageCode?: string;
}): Promise<BffShoppingCartResponse> {
  const searchParams = new URLSearchParams();
  if (params.languageCode)
    searchParams.set('languageCode', params.languageCode);

  const url =
    searchParams.size > 0
      ? `${ENDPOINTS.shoppingCarts}/${params.cartId}/shipping-address?${searchParams.toString()}`
      : `${ENDPOINTS.shoppingCarts}/${params.cartId}/shipping-address`;

  const response = await createBffApi().put<BffShoppingCartResponse>(url, params.address);
  return normalizeBffShoppingCartResponse(response);
}

async function putBffShoppingCartBillingAddress(params: {
  cartId: string;
  address: BffCartAddress;
  languageCode?: string;
}): Promise<BffShoppingCartResponse> {
  const searchParams = new URLSearchParams();
  if (params.languageCode)
    searchParams.set('languageCode', params.languageCode);

  const url =
    searchParams.size > 0
      ? `${ENDPOINTS.shoppingCarts}/${params.cartId}/billing-address?${searchParams.toString()}`
      : `${ENDPOINTS.shoppingCarts}/${params.cartId}/billing-address`;

  const response = await createBffApi().put<BffShoppingCartResponse>(url, params.address);
  return normalizeBffShoppingCartResponse(response);
}

async function createBffShoppingCartPayment(params: {
  cartId: string;
  body: BffCreatePaymentBody;
}): Promise<BffCreatePaymentResponse> {
  return createBffApi().post<BffCreatePaymentResponse>(
    `${ENDPOINTS.shoppingCarts}/${params.cartId}/payment`,
    params.body,
  );
}

async function findBffShoppingCartAddresses(params: {
  query: string;
  countryCode: string;
  languageCode: string;
}): Promise<BffFindAddressesResponse> {
  const searchParams = new URLSearchParams({
    query: params.query,
    countryCode: params.countryCode,
    languageCode: params.languageCode,
  });

  return createBffApi().get<BffFindAddressesResponse>(
    `${ENDPOINTS.shoppingCartAddresses}?${searchParams.toString()}`,
  );
}

async function getBffShoppingCartAddressByReference(params: {
  reference: string;
}): Promise<BffGetAddressByReferenceResponse> {
  const encodedId = encodeURIComponent(params.reference);

  const response = await createBffApi().get<{ data: BffAddressDetailApiRaw }>(
    `${ENDPOINTS.shoppingCartAddresses}/${encodedId}`,
  );

  return {
    data: normalizeBffAddressDetail(response.data),
  };
}

export {
  cancelBffSubscription,
  cancelBooking,
  cancelBookingByCode,
  checkAvailability,
  checkEmailExists,
  createBffShoppingCart,
  createBffShoppingCartPayment,
  createBffStockNotification,
  createBooking,
  createSupportCase,
  findBffShoppingCartAddresses,
  getBffCatalogProducts,
  getBffCatalogSubscriptions,
  getBffFaqs,
  getBffOrder,
  getBffOrderInvoiceDownload,
  getBffOrderInvoices,
  getBffOrders,
  getBffShoppingCart,
  getBffShoppingCartAddressByReference,
  getBffSubscription,
  getBffSubscriptionMembers,
  getBffSubscriptions,
  getBooking,
  getBookingByCode,
  getBookings,
  getLocation,
  getLocations,
  getLocationServices,
  getSocialProviders,
  getSupportConfigurations,
  getUserBookings,
  getUserProfile,
  inviteBffSubscriptionMember,
  linkSocialAccount,
  pauseBffSubscription,
  putBffShoppingCartBillingAddress,
  putBffShoppingCartCoupon,
  putBffShoppingCartProducts,
  putBffShoppingCartShippingAddress,
  putBffShoppingCartSubscriptions,
  putUserProfileBillingAddress,
  putUserProfileShippingAddress,
  redeemBffSubscription,
  refreshToken,
  removeBffSubscriptionMember,
  resetPassword,
  resumeBffSubscription,
  sendPasswordReset,
  signIn,
  signInWithIdToken,
  signUp,
  unlinkSocialAccount,
  updateBooking,
  updateBookingByCode,
  updateUserProfile,
  validateSocialLogin,
};

export type { Booking, Location, ProfileAddress, Service, UserProfile };
